import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { LLMUsage, LLMStreamChunk, LLMStreamResponse, MessageImageInfo } from '../types';
import { supabase } from '../lib/supabase';

// Create axios instance
const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081';

export const apiClient = axios.create({
  baseURL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Supabase auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // Skip session fetch if Authorization header is already set
      if (config.headers.Authorization) {
        return config;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch (error) {
      // Continue with request even if session fetch fails
      // The request might already have an Authorization header
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    // Only log errors that matter
    if (error.response?.status === 401) {
      // Unauthorized - token expired or invalid
      await supabase.auth.signOut();
      window.location.href = '/login';
      toast.error('Session expired. Please login again.');
    } else if (!error.response && !error.request) {
      // Setup error (not network or response error)
      console.error('API setup error:', error.message);
    }
    return Promise.reject(error);
  }
);

// API Service Functions

// Chat API
export const chatApi = {
  // Get all user chats
  getChats: () => apiClient.get('/api/chats'),
  
  // Get specific chat with messages
  getChat: (chatId: string) => apiClient.get(`/api/chats/${chatId}`),

  // Get chat with messages and ramifications in one request (optimized)
  getChatFullData: (chatId: string) => apiClient.get(`/api/chats/${chatId}/full`),

  // Get entire conversation tree data in ONE request (maximum optimization)
  getTreeFullData: (rootChatId: string) => apiClient.get(`/api/chats/tree/${rootChatId}/all-data`),

  // Create new chat
  createChat: (data: { title?: string; parent_chat_id?: string; ramified_from?: string }) =>
    apiClient.post('/api/chats', data),
  
  // Add message to chat
  addMessage: (chatId: string, data: { role: string; content: string; api_provider?: string; web_search_count?: number; citations?: { [key: string]: string } }) =>
    apiClient.post(`/api/chats/${chatId}/messages`, data),

  // Update message content
  updateMessage: (chatId: string, messageId: string, content: string) =>
    apiClient.patch(`/api/chats/${chatId}/messages/${messageId}`, { content }),

  // Update chat title
  updateChatTitle: (chatId: string, title: string) =>
    apiClient.patch(`/api/chats/${chatId}`, { title }),
  
  // Delete entire conversation tree (root chat + all branches)
  deleteConversationTree: (rootChatId: string) =>
    apiClient.delete(`/api/chats/${rootChatId}/tree`),
  
  // Create ramified chat with history inheritance
  createRamifiedChat: (data: { parent_chat_id: string; title?: string; ramified_from?: string }) =>
    apiClient.post('/api/chats/ramify', data),
  
  // Get chat summary (for hover tooltips)
  getChatSummary: (chatId: string) =>
    apiClient.get(`/api/chats/${chatId}/summary`),
  
  // Regenerate chat summary (force refresh)
  regenerateChatSummary: (chatId: string) =>
    apiClient.post(`/api/chats/${chatId}/summary/regenerate`),

  // Get conversation tree summary
  getConversationTreeSummary: (rootChatId: string) =>
    apiClient.get(`/api/chats/${rootChatId}/tree-summary`),

  // Generate conversation tree summary
  generateConversationTreeSummary: (rootChatId: string) =>
    apiClient.post(`/api/chats/${rootChatId}/tree-summary`),
  
  // Collaborative features
  getChatCollaborators: (chatId: string) =>
    apiClient.get(`/api/chats/${chatId}/collaborators`),
  
  addChatCollaborator: (chatId: string, data: { email: string; role?: string }) =>
    apiClient.post(`/api/chats/${chatId}/collaborators`, data),
  
  removeChatCollaborator: (chatId: string, collaboratorId: string) =>
    apiClient.delete(`/api/chats/${chatId}/collaborators/${collaboratorId}`),
  
  acquireChatLock: (chatId: string) =>
    apiClient.post(`/api/chats/${chatId}/lock`),

  // Ramification tracking
  createRamificationLink: (data: {
    parent_chat_id: string;
    parent_message_id: string;
    child_chat_id: string;
    ramified_text: string;
    start_index: number;
    end_index: number;
  }) => apiClient.post('/api/ramifications', data),

  getChatRamifications: (chatId: string) =>
    apiClient.get(`/api/chats/${chatId}/ramifications`),

  getMessageRamifications: (messageId: string) =>
    apiClient.get(`/api/messages/${messageId}/ramifications`),
  
  releaseChatLock: (chatId: string) =>
    apiClient.delete(`/api/chats/${chatId}/lock`),
};

// AI API
export const aiApi = {
  // Unified LLM endpoint (supports multimodal content)
  callLLM: (
    model: string,
    messages: Array<{ role: string; content: any }>, // Accepts both string and ContentBlock[]
    options?: { temperature?: number; max_tokens?: number; top_p?: number },
    webSearch?: boolean
  ) =>
    apiClient.post('/api/llm/chat', {
      model,
      messages,
      options,
      web_search: webSearch || false,
    }, {
      timeout: 240000, // 4 minutes for LLM requests
    }),

  // Streaming LLM endpoint (supports multimodal content)
  callLLMStream: async (
    model: string,
    messages: Array<{ role: string; content: any }>, // Accepts both string and ContentBlock[]
    options?: { temperature?: number; max_tokens?: number; top_p?: number },
    webSearch?: boolean,
    onChunk?: (chunk: LLMStreamChunk) => void,
    onError?: (error: any) => void,
    onComplete?: (fullContent: string, usage: LLMUsage) => void
  ): Promise<LLMStreamResponse> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No authentication session found');
    }
    const token = session.access_token;

    // Use the same baseURL logic as apiClient to avoid double slash issues
    const baseURL = (apiClient.defaults.baseURL || 'http://localhost:8081').replace(/\/$/, '');
    const streamUrl = `${baseURL}/api/llm/chat/stream`;

    const response = await fetch(streamUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        model,
        messages,
        options,
        web_search: webSearch || false,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Failed to get response reader');
    }

    const decoder = new TextDecoder();
    let fullContent = '';
    let usage = {};
    let isAborted = false;

    // Create an AbortController for cleanup
    const abortController = new AbortController();

    try {
      while (true && !isAborted) {
        const { done, value } = await reader.read();
        if (done) break;

        // Check if we should abort
        if (abortController.signal.aborted) {
          isAborted = true;
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'content_delta' && data.content) {
                fullContent += data.content;
                onChunk?.(data);
              } else if (data.type === 'content' && data.content) {
                // Handle non-streaming content (e.g., for web search responses)
                fullContent = data.content;
                onChunk?.({ type: 'content_delta', content: data.content });
              } else if (data.type === 'tool_use_start' && data.content) {
                // Handle web search status updates
                onChunk?.(data);
              } else if (data.type === 'done') {
                usage = data.usage || {};
                const citations = data.citations || {};
                onComplete?.(fullContent, usage);
                return { content: fullContent, usage, citations };
              } else if (data.type === 'error') {
                throw new Error(data.error);
              }
            } catch (parseError) {
              // Skip malformed JSON
              continue;
            }
          }
        }
      }
    } catch (error) {
      onError?.(error);
      throw error;
    } finally {
      // Ensure proper cleanup
      try {
        reader.releaseLock();
      } catch (e) {
        // Reader might already be released
      }

      // Clean up any pending operations
      abortController.abort();
    }

    return { content: fullContent, usage };
  },

  // Get available models
  getModels: () => apiClient.get('/api/llm/models'),

  // Generate chat title using unified LLM
  generateTitleUnified: (
    messages: Array<{ role: string; content: string }>,
    model: string = 'gpt-5-nano'
  ) =>
    apiClient.post('/api/llm/generate-title', {
      messages,
      model,
    }),

  // Upload image for multimodal chat
  uploadImage: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post('/api/llm/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 30000, // 30 seconds for image upload
    });
  },

  // Link uploaded images to a message (for persistence)
  linkImagesToMessage: async (messageId: string, imageIds: string[]) => {
    return apiClient.post(`/api/chats/messages/${messageId}/images`, imageIds);
  },

  // Legacy endpoints (for backward compatibility)
  // Call Claude API (authenticated)
  callClaude: (messages: Array<{ role: string; content: string }>) =>
    apiClient.post('/api/claude', {
      max_tokens: 1000,
      messages,
    }),

  // Call OpenAI API (authenticated)
  callOpenAI: (messages: Array<{ role: string; content: string }>) =>
    apiClient.post('/api/openai', {
      model: 'gpt-5',
      max_tokens: 1000,
      messages,
    }),

  // Generate chat title using AI (legacy)
  generateTitle: (messages: Array<{ role: string; content: string }>, provider?: 'claude' | 'openai') =>
    apiClient.post('/api/generate-title', {
      messages,
      provider: provider || 'claude'
    }),
};

// Payment API
export const paymentApi = {
  // Get pricing plans
  getPricing: () => apiClient.get('/api/payments/pricing'),

  // Get current subscription
  getSubscription: () => apiClient.get('/api/payments/subscription'),

  // Create Stripe customer
  createCustomer: () => apiClient.post('/api/payments/create-customer'),

  // Create subscription (new flow)
  createSubscription: (pricing_plan: string) =>
    apiClient.post('/api/payments/create-subscription', { pricing_plan }),

  // Create complete subscription (customer + payment method + subscription)
  createCompleteSubscription: (data: { pricing_plan: string; payment_method: string }) =>
    apiClient.post('/api/payments/create-complete-subscription', data),

  // Upgrade/downgrade subscription with proration
  upgradeSubscription: (pricing_plan: string) =>
    apiClient.post('/api/payments/upgrade-subscription', { pricing_plan }),

  // Cancel subscription
  cancelSubscription: (data: { reason?: string; cancel_immediately?: boolean }) =>
    apiClient.post('/api/payments/cancel-subscription', data),
  
  // Get billing history
  getBillingHistory: () => apiClient.get('/api/payments/billing-history'),
  
  // Get payment methods
  getPaymentMethods: () => apiClient.get('/api/payments/payment-methods'),
  
  // Create setup intent for saving payment methods
  createSetupIntent: () => apiClient.get('/api/payments/setup-intent'),
  
  // Delete payment method
  deletePaymentMethod: (paymentMethodId: string) =>
    apiClient.delete(`/api/payments/payment-methods/${paymentMethodId}`),
  
  // Get usage information (now includes token-level data)
  getUsageInfo: () => apiClient.get('/api/payments/usage'),

  // Get token usage history for different periods
  getTokenHistory: (days = 7) => apiClient.get(`/api/payments/token-history?days=${days}`),

  // Get dynamic subscription status
  getSubscriptionStatus: () => apiClient.get('/api/payments/subscription-status'),
};

// Sharing API
export const sharingApi = {
  // Share by email
  shareByEmail: (data: {
    root_chat_id: string;
    email: string;
    permission: 'viewer' | 'editor';
    expires_in_days?: number | null;
  }) => apiClient.post('/api/shares/email', data),

  // Create share link
  createShareLink: (data: {
    root_chat_id: string;
    permission: 'viewer' | 'editor';
    expires_in_days?: number | null;
  }) => apiClient.post('/api/shares/link', data),

  // Accept share link
  acceptShare: (share_link: string) =>
    apiClient.post('/api/shares/accept', { share_link }),

  // Get user's shares (owned and received)
  getUserShares: () => apiClient.get('/api/shares/'),

  // Update share permission
  updateSharePermission: (shareId: string, permission: 'viewer' | 'editor') =>
    apiClient.patch(`/api/shares/${shareId}/permission`, { permission }),

  // Revoke share
  revokeShare: (shareId: string) => apiClient.delete(`/api/shares/${shareId}`),

  // Check access level for a chat
  checkAccess: (chatId: string) => apiClient.get(`/api/shares/access/${chatId}`)
};

// Feedback API
export const feedbackApi = {
  // Submit new feedback
  submitFeedback: (feedbackData: {
    feedback_type: string;
    title: string;
    description: string;
    rating?: number;
  }) => apiClient.post('/feedback', feedbackData),

  // Get user's feedback
  getUserFeedback: (limit: number = 50) => 
    apiClient.get(`/feedback/my?limit=${limit}`),

  // Get all feedback (admin)
  getAllFeedback: (params?: {
    feedback_type?: string;
    status_filter?: string;
    limit?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.feedback_type) queryParams.append('feedback_type', params.feedback_type);
    if (params?.status_filter) queryParams.append('status_filter', params.status_filter);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    return apiClient.get(`/feedback/all?${queryParams.toString()}`);
  },

  // Update feedback status (admin)
  updateFeedbackStatus: (feedbackId: string, status: string) => 
    apiClient.patch(`/feedback/${feedbackId}/status`, { status }),
};

// Notes API
export const notesApi = {
  // Get note for a specific chat
  getNoteForChat: (chatId: string) =>
    apiClient.get(`/api/chats/${chatId}/note`),

  // Create or update note for a chat
  saveNote: (chatId: string, data: { title?: string; content: string }) =>
    apiClient.post(`/api/chats/${chatId}/note`, data),

  // Update specific fields of a note
  updateNote: (chatId: string, data: { title?: string; content?: string }) =>
    apiClient.patch(`/api/chats/${chatId}/note`, data),

  // Delete note for a chat
  deleteNote: (chatId: string) =>
    apiClient.delete(`/api/chats/${chatId}/note`),

  // Upload image to a note
  uploadImage: async (noteId: string, file: File | Blob, filename: string = 'pasted-image.png') => {
    // Use axios to send raw binary data
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    const response = await apiClient.post(
      `/api/notes/${noteId}/images`,
      file, // Send raw binary data
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': file.type || 'image/png',
          'X-Filename': filename
        }
      }
    );

    return response.data;
  },

  // Delete an image from a note
  deleteImage: (imageId: string) =>
    apiClient.delete(`/api/notes/images/${imageId}`),

  // Get all user notes
  getUserNotes: (limit: number = 50) =>
    apiClient.get(`/api/notes?limit=${limit}`),

  // Search notes by title for wikilink autocomplete
  searchNotesByTitle: (query: string, limit: number = 10) =>
    apiClient.get(`/api/notes/search?q=${encodeURIComponent(query)}&limit=${limit}`),

  // Get backlinks for a note
  getBacklinks: (noteId: string) =>
    apiClient.get(`/api/notes/${noteId}/backlinks`),

  // Get notes graph data for visualization
  getNotesGraphData: () =>
    apiClient.get('/api/notes/graph/data'),

  // Create note (optionally linked to a chat)
  createNote: (data: { title: string; content: string; chat_id?: string }) =>
    apiClient.post('/api/notes', data),

  // Get note by ID
  getNoteById: (noteId: string) =>
    apiClient.get(`/api/notes/${noteId}`),

  // Update note by ID
  updateNoteById: (noteId: string, data: { title: string; content: string }) =>
    apiClient.put(`/api/notes/${noteId}`, data),

  // Delete note by ID
  deleteNoteById: (noteId: string) =>
    apiClient.delete(`/api/notes/${noteId}`),

  // Link note to chat
  linkNoteToChat: (noteId: string, chatId: string) =>
    apiClient.post(`/api/notes/${noteId}/link-to-chat/${chatId}`),

  // Unlink note from chat (now requires chatId for many-to-many)
  unlinkNoteFromChat: (noteId: string, chatId: string) =>
    apiClient.post(`/api/notes/${noteId}/unlink-from-chat/${chatId}`),

  // Get all chats linked to a note
  getLinkedChatsForNote: (noteId: string) =>
    apiClient.get(`/api/notes/${noteId}/linked-chats`),

  // Get all notes linked to a chat
  getLinkedNotesForChat: (chatId: string) =>
    apiClient.get(`/api/chats/${chatId}/linked-notes`),
};

// Tags API
export const tagsApi = {
  // Get all user tags
  getUserTags: () => apiClient.get('/api/tags'),

  // Create a new tag
  createTag: (data: { name: string; color?: string }) =>
    apiClient.post('/api/tags', data),

  // Update a tag
  updateTag: (tagId: string, data: { name?: string; color?: string }) =>
    apiClient.patch(`/api/tags/${tagId}`, data),

  // Delete a tag
  deleteTag: (tagId: string) =>
    apiClient.delete(`/api/tags/${tagId}`),

  // Get tags for a specific entity (chat or note)
  getChatTags: (chatId: string) =>
    apiClient.get(`/api/chats/${chatId}/tags`),

  // Add a tag to an entity
  addTagToChat: (chatId: string, data: { tag_id: string }) =>
    apiClient.post(`/api/chats/${chatId}/tags`, data),

  // Remove a tag from an entity
  removeTagFromChat: (chatId: string, tagId: string) =>
    apiClient.delete(`/api/chats/${chatId}/tags/${tagId}`),

  // Note-specific tag operations
  getNoteTags: (noteId: string) =>
    apiClient.get(`/api/notes/${noteId}/tags`),

  addTagToNote: (noteId: string, data: { tag_id: string }) =>
    apiClient.post(`/api/notes/${noteId}/tags`, data),

  removeTagFromNote: (noteId: string, tagId: string) =>
    apiClient.delete(`/api/notes/${noteId}/tags/${tagId}`),

  // Get all entities that have a specific tag (returns both notes and chats)
  getEntitiesByTag: (tagId: string) =>
    apiClient.get(`/api/tags/${tagId}/entities`),

  // Search notes by multiple tags with AND/OR logic
  searchNotesByTags: (tagIds: string[], logic: 'AND' | 'OR' = 'AND') =>
    apiClient.get(`/api/search/notes-by-tags?tag_ids=${tagIds.join(',')}&logic=${logic}`),

  // Search chats by multiple tags with AND/OR logic
  searchChatsByTags: (tagIds: string[], logic: 'AND' | 'OR' = 'AND') =>
    apiClient.get(`/api/search/chats-by-tags?tag_ids=${tagIds.join(',')}&logic=${logic}`),
};

// Types for API responses
export interface Chat {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  parent_chat_id?: string;
  ramified_from?: string;
  is_shareable: boolean;
  current_editor?: string;
  lock_expires_at?: string;
  access_level?: 'owner' | 'viewer' | 'editor';
  is_shared?: boolean;
  last_message?: {
    role: string;
    content: string;
    timestamp: string;
  };
}

export interface ChatParticipant {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role: 'owner' | 'collaborator' | 'viewer';
  joined_at: string;
}

export interface ChatLockStatus {
  message: string;
  locked: boolean;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  api_provider?: string;
  web_search_count?: number;  // Number of web searches performed
  citations?: { [key: string]: string };  // URL to citation number mapping
  images?: MessageImageInfo[];  // Persisted images from database
}

export interface ChatSummary {
  message: string;
  summary: string;
  chat_id: string;
  updated: boolean;
}

export interface ConversationTreeSummary {
  message: string;
  summary: string;
  root_chat_id: string;
  message_count: number;
  chat_count: number;
  updated: boolean;
}

export interface ConversationTreeSummaryGet {
  summary?: string;
  root_chat_id: string;
  exists: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface Subscription {
  id: string;
  pricing_plan: string;
  status: string;
  current_period_end?: string;
  cancel_at_period_end: boolean;
  plan_details?: {
    name: string;
    price: number;
    currency: string;
    features: {
      max_branches: number;
      api_calls_per_day: number;
      max_chats: number;
    };
  };
}

export interface Share {
  id: string;
  chat_id: string;
  share_token: string;
  title?: string;
  description?: string;
  permission: string;
  visibility: string;
  status: string;
  expires_at?: string;
  view_count: number;
  unique_viewers: number;
  created_at: string;
  last_viewed_at?: string;
  share_url: string;
}

export interface SharedChatData {
  chat: {
    id: string;
    title: string;
    created_at: string;
    parent_chat_id?: string;
    ramified_from?: string;
  };
  share_info: {
    title?: string;
    description?: string;
    permission: string;
    visibility: string;
    allow_comments: boolean;
    created_at: string;
    view_count: number;
  };
  messages: Message[];
}

// Feedback interfaces
export interface FeedbackData {
  feedback_type: string;
  title: string;
  description: string;
  rating?: number;
}

export interface FeedbackResponse {
  message: string;
  feedback: {
    id: string;
    feedback_type: string;
    title: string;
    description: string;
    rating?: number;
    status: string;
    created_at: string;
    updated_at: string;
  };
}

export interface FeedbackInfo {
  id: string;
  feedback_type: string;
  title: string;
  description: string;
  rating?: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface FeedbackListResponse {
  feedback: FeedbackInfo[];
}

// Note interfaces
export interface NoteInfo {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
  chat?: {
    id: string;
    title: string;
  };
}

export interface NoteResponse {
  message: string;
  note: NoteInfo;
}

export interface NotesListResponse {
  message: string;
  notes: NoteInfo[];
}

// Tag interfaces
export interface TagInfo {
  id: string;
  name: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface TagResponse {
  message: string;
  tag: TagInfo;
}

export interface TagsListResponse {
  message: string;
  tags: TagInfo[];
}

export interface TagAssociationInfo {
  id: string;
  tag: TagInfo;
  entity_type: string;
  entity_id: string;
  created_at: string;
}

export interface TagAssociationResponse {
  message: string;
  association: TagAssociationInfo;
}

export interface EntityTagsResponse {
  message: string;
  tags: TagInfo[];
}

export interface EntitiesByTagResponse {
  message: string;
  entities: {
    chats: Array<{
      id: string;
      title: string;
      created_at: string;
      updated_at: string;
    }>;
    notes: Array<{
      id: string;
      title: string;
      created_at: string;
      updated_at: string;
    }>;
  };
}

// Search interfaces
export interface SearchRequest {
  query: string;
  limit?: number;
  fuzzy_threshold?: number;
  content_types?: string[];
}

export interface SearchResult {
  id: string;
  content_type: 'chat' | 'message' | 'note';
  title: string;
  excerpt: string;
  highlighted_excerpt: string;
  rank: number;
  chat_id: string;
  created_at: string;
  updated_at: string;
}

export interface SearchResponse {
  message: string;
  results: SearchResult[];
  total_count: number;
  query: string;
  suggestions: string[];
  execution_time_ms: number;
}

export interface SearchSuggestionsResponse {
  message: string;
  query: string;
  suggestions: string[];
}

// Search API
export const searchApi = {
  // Main search endpoint
  search: (params: SearchRequest) =>
    apiClient.post('/api/search/', params),

  // Search with GET parameters (for URL sharing)
  searchGet: (params: {
    q: string;
    limit?: number;
    fuzzy_threshold?: number;
    content_types?: string;
  }) => apiClient.get('/api/search/', { params }),

  // Get spelling suggestions
  getSuggestions: (query: string) =>
    apiClient.post('/api/search/suggestions', { query }),

  // Get suggestions with GET
  getSuggestionsGet: (q: string) =>
    apiClient.get('/api/search/suggestions', { params: { q } }),

  // Get popular searches
  getPopularSearches: (limit = 5) =>
    apiClient.get('/api/search/popular', { params: { limit } }),

  // Reindex content
  reindexContent: (contentType: 'chat' | 'message' | 'note', contentId: string) =>
    apiClient.post(`/api/search/reindex/${contentType}/${contentId}`)
};